import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  GridLegacy as Grid,
  Card,
  CardContent,
  IconButton,
  Backdrop,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import type { Task } from '../../types';
import { apiRequest } from '../../utils/api';
import { formatDateLocal } from '../../utils/dateUtils';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TaskModal from '../../components/TaskModal';

export type TaskStatus = 'TO DO' | 'IN PROGRESS' | 'COMPLETE' | 'DONE';

interface DateViewProps {
  tasks: Task[];
  onTasksChange: (tasks: Task[]) => void;
  setSavingTask?: (saving: boolean) => void;
}

export default function DateView({ tasks: _tasks, onTasksChange, setSavingTask }: DateViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [initialTaskDate, setInitialTaskDate] = useState<Date | undefined>(undefined);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [confirmMoveToDoneOpen, setConfirmMoveToDoneOpen] = useState(false);
  const [alertMoveToDoneOpen, setAlertMoveToDoneOpen] = useState(false);
  const [pendingMoveTask, setPendingMoveTask] = useState<{ taskId: number; newStatus: TaskStatus } | null>(null);
  const [editDoneTaskAlertOpen, setEditDoneTaskAlertOpen] = useState(false);
  const [jobStatusChangeAlertOpen, setJobStatusChangeAlertOpen] = useState(false);
  const [jobStatusChangeInfo, setJobStatusChangeInfo] = useState<{ oldStatus: string; newStatus: string } | null>(null);


  // Load tasks for the selected date
  useEffect(() => {
    const loadTasksForDate = async () => {
      setLoadingTasks(true);
      try {
        const dateStr = formatDateLocal(currentDate);
        const response = await apiRequest(`/tasks?date=${dateStr}`, { method: 'GET' });
        const tasksData = (response.data || []) as Task[];
        setTasks(tasksData);
        // Also update parent component
        onTasksChange(tasksData);
      } catch (error) {
        console.error('Error loading tasks:', error);
        setTasks([]);
        onTasksChange([]);
      } finally {
        setLoadingTasks(false);
      }
    };

    loadTasksForDate();
  }, [currentDate, onTasksChange]);


  // Get tasks for each column (tasks are already filtered by date from backend)
  const getTasksByStatus = (status: TaskStatus): Task[] => {
    return tasks.filter((task) => task.status === status);
  };


  // Handle adding new task
  const handleAddTask = () => {
    setEditingTask(null);
    setInitialTaskDate(currentDate);
    setOpenDialog(true);
  };

  // Handle editing task
  const handleEditTask = (task: Task) => {
    // Prevent editing DONE tasks
    if (task.status === 'DONE') {
      setEditDoneTaskAlertOpen(true);
      return;
    }
    setEditingTask(task);
    setOpenDialog(true);
  };

  // Handle deleting task
  const handleDeleteTask = async (taskId: number) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await apiRequest(`/tasks/${taskId}`, { method: 'DELETE' });
        // Reload tasks for the current date to get the updated list
        const dateStr = formatDateLocal(currentDate);
        const refreshResponse = await apiRequest(`/tasks?date=${dateStr}`, { method: 'GET' });
        const refreshedTasks = (refreshResponse.data || []) as Task[];
        setTasks(refreshedTasks);
        onTasksChange(refreshedTasks);
      } catch (error) {
        console.error('Error deleting task:', error);
        alert('Failed to delete task. Please try again.');
      }
    }
  };

  // Refresh tasks after operations
  const refreshTasks = async () => {
    const dateStr = formatDateLocal(currentDate);
    try {
      const response = await apiRequest(`/tasks?date=${dateStr}`, { method: 'GET' });
      const tasksData = (response.data || []) as Task[];
      setTasks(tasksData);
      onTasksChange(tasksData);
    } catch (error) {
      console.error('Error refreshing tasks:', error);
    }
  };

  // Handle saving task (called from TaskModal)
  const handleSaveTask = async (taskData: any) => {
    try {
      setSavingTask?.(true);
      if (editingTask) {
        // Update existing task
        const { workLocation, ...taskPayload } = taskData;
        taskPayload.hasWorkLocation = workLocation?.hasWorkLocation ?? false;
        await apiRequest(`/tasks/${editingTask.id}`, {
          method: 'PUT',
          body: JSON.stringify(taskPayload),
        });

        // Handle manual work location (backend handles geocoding when hasWorkLocation=false)
        if (workLocation?.hasWorkLocation && workLocation.address && workLocation.currentUserId) {
          if (workLocation.workLocationId) {
            try {
              await apiRequest(`/work-locations/${workLocation.workLocationId}`, {
                method: 'PUT',
                body: JSON.stringify({
                  date: workLocation.taskDate,
                  location: workLocation.address,
                  latitude: workLocation.latitude,
                  longitude: workLocation.longitude,
                  userId: workLocation.currentUserId,
                  taskId: editingTask.id,
                }),
              });
            } catch (error) {
              console.error('Error updating work location:', error);
            }
          } else {
            try {
              await apiRequest('/work-locations', {
                method: 'POST',
                body: JSON.stringify({
                  date: workLocation.taskDate,
                  location: workLocation.address,
                  latitude: workLocation.latitude,
                  longitude: workLocation.longitude,
                  userId: workLocation.currentUserId,
                  taskId: editingTask.id,
                }),
              });
            } catch (error) {
              console.error('Error creating work location:', error);
            }
          }
        }
      } else {
        // Create new task
        const { workLocation, ...taskPayload } = taskData;
        taskPayload.status = 'TO DO';
        taskPayload.hasWorkLocation = workLocation?.hasWorkLocation ?? false;

        const response = await apiRequest('/tasks', {
          method: 'POST',
          body: JSON.stringify(taskPayload),
        });

        const newTask = response.data as Task;

        // Create manual work location if specified (backend handles geocoding otherwise)
        if (workLocation?.hasWorkLocation && workLocation.address && workLocation.currentUserId) {
          try {
            await apiRequest('/work-locations', {
              method: 'POST',
              body: JSON.stringify({
                date: workLocation.taskDate,
                location: workLocation.address,
                latitude: workLocation.latitude,
                longitude: workLocation.longitude,
                userId: workLocation.currentUserId,
                taskId: newTask.id,
              }),
            });
          } catch (error) {
            console.error('Error creating work location:', error);
          }
        }
      }
      
      // Refresh tasks
      await refreshTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      throw error; // Re-throw so TaskModal can handle it
    } finally {
      setSavingTask?.(false);
    }
  };

  // Handle moving task between columns
  const handleMoveTask = async (taskId: number, newStatus: TaskStatus) => {
    // Check if moving from COMPLETE to DONE - show confirmation first
    const task = tasks.find(t => t.id === taskId);
    if (task && task.status === 'COMPLETE' && newStatus === 'DONE') {
      setPendingMoveTask({ taskId, newStatus });
      setConfirmMoveToDoneOpen(true);
      return;
    }

    // For other moves, proceed directly
    await performMoveTask(taskId, newStatus);
  };

  // Perform the actual task move
  const performMoveTask = async (taskId: number, newStatus: TaskStatus) => {
    try {
      setSavingTask?.(true);
      const response = await apiRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });

      const taskResponse = response.data as Task & {
        jobStatusUpdated?: boolean;
        oldJobStatus?: string;
        newJobStatus?: string;
      };

      // Reload tasks for the current date to get the updated list
      const dateStr = formatDateLocal(currentDate);
      const refreshResponse = await apiRequest(`/tasks?date=${dateStr}`, { method: 'GET' });
      const refreshedTasks = (refreshResponse.data || []) as Task[];
      setTasks(refreshedTasks);
      onTasksChange(refreshedTasks);

      // If moving to DONE, show alert modal
      if (newStatus === 'DONE') {
        setAlertMoveToDoneOpen(true);
      }

      // Check if job status was updated
      if (taskResponse.jobStatusUpdated && taskResponse.oldJobStatus && taskResponse.newJobStatus) {
        setJobStatusChangeInfo({
          oldStatus: taskResponse.oldJobStatus,
          newStatus: taskResponse.newJobStatus,
        });
        setJobStatusChangeAlertOpen(true);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status. Please try again.');
    } finally {
      setSavingTask?.(false);
    }
  };

  // Handle confirmation to move to DONE
  const handleConfirmMoveToDone = () => {
    setConfirmMoveToDoneOpen(false);
    if (pendingMoveTask) {
      performMoveTask(pendingMoveTask.taskId, pendingMoveTask.newStatus);
      setPendingMoveTask(null);
    }
  };

  // Get day of week
  const getDayOfWeek = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  // Format date for display (e.g., "January 15, 2024")
  const getFormattedDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const todoTasks = getTasksByStatus('TO DO');
  const inProgressTasks = getTasksByStatus('IN PROGRESS');
  const completeTasks = getTasksByStatus('COMPLETE');
  const doneTasks = getTasksByStatus('DONE');


  return (
    <Box>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loadingTasks}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading tasks...</Typography>
        </Box>
      </Backdrop>

      {/* Date Display - Top Bar */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          boxShadow: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Date and Day of Week - Left */}
          <Box>
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              {getFormattedDate(currentDate)}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                opacity: 0.9,
                fontWeight: 400,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {getDayOfWeek(currentDate)}
            </Typography>
          </Box>
          
          {/* Calendar Icon - Right */}
          <IconButton 
            onClick={() => setOpenCalendarDialog(true)}
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease-in-out',
              p: 2,
            }}
          >
            <CalendarTodayIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
          </IconButton>
        </Box>
      </Paper>

      {/* Kanban Board */}
      <Grid container spacing={3}>
        {/* TO DO Column */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 600 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                TO DO
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={handleAddTask}
              >
                Add Task
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {todoTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  onMove={handleMoveTask}
                  canMoveForward={true}
                />
              ))}
              {todoTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No tasks in TO DO
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* IN PROGRESS Column */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 600 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                IN PROGRESS
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {inProgressTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  onMove={handleMoveTask}
                  canMoveForward={true}
                  canMoveBackward={true}
                />
              ))}
              {inProgressTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No tasks in progress
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* COMPLETE Column */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 600 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                COMPLETE
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {completeTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  onMove={handleMoveTask}
                  canMoveForward={true}
                  canMoveBackward={true}
                />
              ))}
              {completeTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No completed tasks
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* DONE Column */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 600 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                DONE
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {doneTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  onEdit={handleEditTask}
                  onDelete={handleDeleteTask}
                  onMove={handleMoveTask}
                  canMoveBackward={true}
                />
              ))}
              {doneTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No finished tasks
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add/Edit Task Dialog */}
      <TaskModal
        open={openDialog}
        editingTask={editingTask}
        initialDate={initialTaskDate}
        onClose={() => {
          setOpenDialog(false);
          setEditingTask(null);
          setInitialTaskDate(undefined);
        }}
        onSave={handleSaveTask}
        onRefreshTasks={refreshTasks}
      />

      {/* Calendar Dialog */}
      <Dialog 
        open={openCalendarDialog} 
        onClose={() => setOpenCalendarDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Date</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={currentDate}
              onChange={(newValue) => {
                if (newValue) {
                  setCurrentDate(newValue);
                  setOpenCalendarDialog(false);
                  // Tasks will be reloaded automatically via useEffect when currentDate changes
                }
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCalendarDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Moving to DONE */}
      <Dialog
        open={confirmMoveToDoneOpen}
        onClose={() => {
          setConfirmMoveToDoneOpen(false);
          setPendingMoveTask(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Move to DONE</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to move this task from COMPLETE to DONE?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Once a task is marked as DONE, it cannot be edited or moved to another status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmMoveToDoneOpen(false);
            setPendingMoveTask(null);
          }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmMoveToDone} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Modal after Moving to DONE */}
      <Dialog
        open={alertMoveToDoneOpen}
        onClose={() => setAlertMoveToDoneOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Task Marked as DONE</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            The task has been successfully moved to DONE status.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This task can no longer be edited or moved to another status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertMoveToDoneOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Modal for trying to edit DONE task */}
      <Dialog
        open={editDoneTaskAlertOpen}
        onClose={() => setEditDoneTaskAlertOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cannot Edit DONE Task</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tasks that are DONE cannot be edited.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Once a task is marked as DONE, it cannot be modified or moved to another status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDoneTaskAlertOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Modal for Job Status Change */}
      <Dialog
        open={jobStatusChangeAlertOpen}
        onClose={() => {
          setJobStatusChangeAlertOpen(false);
          setJobStatusChangeInfo(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Job Status Updated</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            All tasks for this job have been completed.
          </Alert>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Job Status Changed:</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {jobStatusChangeInfo && (
              <>
                {jobStatusChangeInfo.oldStatus} → {jobStatusChangeInfo.newStatus}
              </>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: jobStatusChangeInfo?.newStatus === 'Finish' ? 2 : 0 }}>
            The job status has been automatically updated because all rooms for this job type are now DONE.
          </Typography>
          {jobStatusChangeInfo?.newStatus === 'Finish' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              The job has been automatically set to inactive since it has reached the "Finish" status.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setJobStatusChangeAlertOpen(false);
              setJobStatusChangeInfo(null);
            }} 
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Task Card Component
interface TaskCardProps {
  task: Task;
  onEdit: (task: Task) => void;
  onDelete: (taskId: number) => void;
  onMove: (taskId: number, newStatus: TaskStatus) => void;
  canMoveForward?: boolean;
  canMoveBackward?: boolean;
}

function TaskCard({ task, onEdit, onDelete, onMove, canMoveForward, canMoveBackward }: TaskCardProps) {
  const isDone = task.status === 'DONE';
  
  return (
    <Card sx={{ '&:hover': { boxShadow: 3 } }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
            {task.title}
          </Typography>
          <Box>
            <IconButton 
              size="small" 
              onClick={() => onEdit(task)}
              disabled={isDone}
              title={isDone ? 'Tasks that are DONE cannot be edited' : 'Edit task'}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(task.id)} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        {task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {task.description}
          </Typography>
        )}
        {task.teamLeaderName && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            <strong>Team Leader:</strong> {task.teamLeaderName}
          </Typography>
        )}
        {task.memberNames && task.memberNames.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <strong>Members:</strong> {task.memberNames.join(', ')}
          </Typography>
        )}
        {(task.endDate) ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <strong>Date Range:</strong> {task.taskDate ? new Date(task.taskDate).toLocaleDateString() : ''} - {new Date(task.endDate).toLocaleDateString()}
          </Typography>
        ) : task.taskDate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            <strong>Date:</strong> {new Date(task.taskDate).toLocaleDateString()}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {canMoveBackward && !isDone && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (task.status === 'DONE') {
                  onMove(task.id, 'COMPLETE');
                } else if (task.status === 'COMPLETE') {
                  onMove(task.id, 'IN PROGRESS');
                } else if (task.status === 'IN PROGRESS') {
                  onMove(task.id, 'TO DO');
                }
              }}
            >
              ← Move Back
            </Button>
          )}
          {canMoveForward && !isDone && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                if (task.status === 'TO DO') {
                  onMove(task.id, 'IN PROGRESS');
                } else if (task.status === 'IN PROGRESS') {
                  onMove(task.id, 'COMPLETE');
                } else if (task.status === 'COMPLETE') {
                  onMove(task.id, 'DONE');
                }
              }}
            >
              Move Forward →
            </Button>
          )}
          {isDone && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
              This task is DONE and cannot be edited or moved.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
